import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { maxText, numberFormat, safeParseInt, setDocumentTitle } from 'shared/util'

import { useTenant } from 'features/tenant/context'
import { SearchProvider } from 'features/search/context'
import { KojinResult, useListKojin } from 'interface/kojin'
import { SearchDetailResult, useGetSearchDetail } from 'interface/search'
import { TargetListResult, useGetTargetList } from 'interface/search/targetList'
import { MrpProjectResult, useGetMrpProject } from 'interface/carenet/mrp'
import { LvProjectResult, useGetLvProject } from 'interface/carenet/lv'

import { Loading } from 'components/controls/loading'
import { ErrorBlock, ErrorInfo } from 'components/controls/errorInfo'

import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle
} from "react-resizable-panels"

import {
  Paper,
  Stack,
  IconButton,
  StackProps
} from '@mui/material'

import {
  KeyboardDoubleArrowLeft as ArrowLeftIcon,
  KeyboardDoubleArrowRight as ArrowRightIcon,
} from '@mui/icons-material'

import { BreadCrumbItem } from 'components/topBar/types'
import { ValueSpan } from 'components/controls/valueSpan'

import { DashBoard } from 'features/dashBoard'
import { DashBoardBar } from 'features/dashBoard/components/dashBoardBar'
import { DashBoardConditions } from 'features/dashBoard/types'
import { useDashBoard } from 'features/dashBoard/hooks'
import { applyDashBoardCondition, filter, initDashBoardConditions } from 'features/dashBoard/util'

import { kojinDashBoardConfigure } from './configure'
import { KojinList } from './components/list'
import { TargetListExtendBar } from './components/targetListExtendBar'
import { useExtendThreshold } from './components/targetListExtendBar/hooks'

import { KojinPreviewDialog } from 'features/kojin/dialog/preview'
import { useKojinPreviewDialog } from 'features/kojin/dialog/preview/hooks'

import { usePrediction } from 'features/prediction/hooks'
import { PredictionDialog } from 'features/prediction/dialog'
import { PredictionCondition } from 'interface/carenet/prediction'
import { PredictionBtn } from 'features/prediction/components/predictionBtn'
import { PredictionHistoryDialog } from 'features/prediction/dialog/history'

import { useTopBar } from 'components/topBar/container'
import { useSideMenu } from 'components/sideMenu/container'
import { useEdittableKojinList, EdittableKojinListProvider } from 'features/kojin/context/list'

import { KojinViewMode } from './types'
import { useGetEvaluationForm } from 'interface/evaluationForm'

type KojinAnalyticsPageProps = {
  searchId: number
  topBarState: ReturnType<typeof useTopBar>
  sideMenuState: ReturnType<typeof useSideMenu>
} & StackProps
export const KojinAnalyticsPage = ({
  searchId,
  ...others
}: KojinAnalyticsPageProps) => {
  const [params] = useSearchParams()
  const targetListId = safeParseInt(params.get('listId')) ?? null
  const mrpProjectId = safeParseInt(params.get('mrp')) ?? null
  const lvProjectCode = params.get('lv') ?? null

  const searchStatus = useGetSearchDetail(searchId)
  const evaluationFormStatus = useGetEvaluationForm(searchStatus.data?.evaluationFormId)
  const targetListStatus = useGetTargetList(searchId, targetListId)
  const mrpProjectStatus = useGetMrpProject(mrpProjectId)
  const lvProjectStatus = useGetLvProject(lvProjectCode)
  
  if (searchStatus.loading || evaluationFormStatus.loading) return <Loading/>
  if (searchStatus.error) return <ErrorInfo error={searchStatus.error}/>
  if (!searchStatus.data) return <></>
  if (evaluationFormStatus.error) return <ErrorInfo error={evaluationFormStatus.error}/>
  if (targetListId) {
    if (targetListStatus.loading) return <Loading/>
    if (targetListStatus.error) return <ErrorInfo error={targetListStatus.error}/>
    if (!targetListStatus.data) return <></>
  }
  if (mrpProjectId) {
    if (mrpProjectStatus.loading) return <Loading/>
    if (mrpProjectStatus.error) return <ErrorInfo error={mrpProjectStatus.error}/>
    if (!mrpProjectStatus.data) return <></>
  }
  if (lvProjectCode) {
    if (lvProjectStatus.loading) return <Loading/>
    if (lvProjectStatus.error) return <ErrorInfo error={lvProjectStatus.error}/>
    if (!lvProjectStatus.data) return <></>
  }
  return (
    <SearchProvider
      search={searchStatus.data}
      evaluationForm={evaluationFormStatus.data}
      targetList={targetListStatus.data}
      mrpProject={mrpProjectStatus.data}
      lvProject={lvProjectStatus.data}
    >
      <KojinAnalyticsPageWrapper
        search={searchStatus.data}
        targetList={targetListStatus.data}
        mrpProject={mrpProjectStatus.data}
        lvProject={lvProjectStatus.data}
        {...others}
      />
    </SearchProvider>
  )
}

type KojinAnalyticsPageCoreProps = {
  search: SearchDetailResult
  targetList: TargetListResult | null
  mrpProject: MrpProjectResult | null
  lvProject: LvProjectResult | null
} & Omit<KojinAnalyticsPageProps, "searchId">

const KojinAnalyticsPageWrapper = ({ ...props }: KojinAnalyticsPageCoreProps) => {
  const kojinListState = useListKojin(props.search.searchId, {
    targetListId: props.targetList?.listId,
    mrpProjectId: props.mrpProject?.projectId,
    lvProjectCode: props.lvProject?.projectCode
  }, {
    updateTime: maxText(props.targetList?.updateTime, props.search.updateTime) ?? undefined
  })
  return (
    <EdittableKojinListProvider kojinListState={kojinListState}>
      <KojinAnalyticsPageCore {...props}/>
    </EdittableKojinListProvider>
  )
}

const KojinAnalyticsPageCore = ({
	topBarState: { setBreadCrumbs },
	sideMenuState: { setActivePage },
  search,
  targetList,
  mrpProject,
  lvProject,
  ...others
}: KojinAnalyticsPageCoreProps) => {
  const tenant = useTenant()
  const [params, setParams] = useSearchParams()

  const [conditions, setConditions] = useState<DashBoardConditions>(
    initDashBoardConditions(kojinDashBoardConfigure, params)
  )
  const [profileId, setProfileId] = useState<number | null>(
    safeParseInt(params.get('profileId')) ?? null
  )
  const [modal, setModal] = useState<{
    type: 'prediction' | 'predictionHistory',
    predictionCondition?: PredictionCondition
  } | null>(null)
  const [panelViewMode, setPanelViewMode] = useState<KojinViewMode>('full')
  const [panelViewSize, setPanelViewSize] = useState<number>(66)

  const refBoard = useRef<ImperativePanelHandle>(null)
  const refList = useRef<ImperativePanelHandle>(null)
  
	const {
		kojinCode: selectedKojinCode,
		authorId: selectedAuthorId,
		openKojin,
		close: closeKojinDialog
	} = useKojinPreviewDialog()

  const {
    loading,
    error,
    progress,
    kojinList: sourceList
  } = useEdittableKojinList() ?? {}

  const dashBoard = useDashBoard<KojinResult>({
    profileKey: 'kojin',
    configure: kojinDashBoardConfigure,
    conditions, setConditions,
    profileId, setProfileId,
    searchId: search.searchId,
    mrpProjectId: mrpProject?.projectId,
    lvProjectCode: lvProject?.projectCode
  })

  const kojinList = useMemo(
    () => {
      if (loading || !sourceList) return []
      let kojinList = sourceList
      const mrpLog = dashBoard.subSource.mrp.dataState.data?.list
      if (mrpLog) {
        kojinList = kojinList.map(kojin => {
          const log = mrpLog.find(log => log.kojinCode === kojin.kojinCode)
          if (!log) return kojin
          return { ...kojin, mrp: log }            
        })
      }
      const lvLog = dashBoard.subSource.lv.dataState.data?.list
      if (lvLog) {
        kojinList = kojinList.map(kojin => {
          const log = lvLog.find(log => log.kojinCode === kojin.kojinCode)
          if (!log) return kojin
          return { ...kojin, lv: log }
        })
      }
      return kojinList
    },
    [loading, sourceList, dashBoard.subSource.lv.dataState, dashBoard.subSource.mrp.dataState]
  )

  const {
    setExtendThreshold, extendThresholdRate,
		counts, limittedList, extended
  } = useExtendThreshold({ kojinList })

  const {
    filteredList, // DashBoardのフィルターがかかったもの
    conditionText,
    conditionHash
  } = useMemo(
    () => {
      const conditionTextLines = []
      if (extended > 0) conditionTextLines.push(`拡張=${numberFormat(extended)}件`)
      conditionTextLines.push(...Object.entries(dashBoard.conditions).map(
        ([axisKey, condition]) => {
          const axis = kojinDashBoardConfigure.axis[axisKey]
          const prefix = (axis ? `${axis.title} = ` : '')
          const value = condition.label + (
            condition.over ? condition.not ? '未満' : '以上' : 
            condition.under ? condition.not ? 'より上' : '以下' : 
            condition.not ? '以外' : '')
          return prefix + value
        }
      ))
      const conditionText = conditionTextLines.join(' and ')
      const conditionHash = JSON.stringify({
        searchId: search.searchId,
        listId: targetList?.listId,
        mrpProjectId: mrpProject?.projectId,
        lvProjectCode: lvProject?.projectCode,
        extended,
        conditions: dashBoard.conditions
      })
      const filteredList = limittedList?.filter(
        (kojin) => filter(
          kojin,
          kojinDashBoardConfigure,
          dashBoard.conditions,
          undefined,
          targetList ?? undefined
        )
      ) ?? []
      return { filteredList, conditionText, conditionHash }
    },
    [search, targetList, limittedList, mrpProject, lvProject, extended, dashBoard.conditions]
  )

  const predictionState = usePrediction({
    searchId: search.searchId,
    targetListId: targetList?.listId ?? null,
    kojinList: filteredList,
    conditionText,
    conditionHash
  })
  const openPredictionDialog = useCallback(
    () => setModal({ type: 'prediction' }),
    [setModal]
  )
  const openPredictionHistoryDialog = useCallback(
    () => setModal({ type: 'predictionHistory' }),
    [setModal]
  )
  const onSelectedPredictionCondition = useCallback(
    (predictionCondition: PredictionCondition) => {
      setModal({ type: 'prediction', predictionCondition })
    },
    [setModal]
  )

  useEffect(
    () => setParams((prev) => {
      if (profileId) {
        prev.set('profileId', String(profileId))
      } else {
        prev.delete('profileId')
      }
      return prev
    }),
    [profileId, setParams]
  )
  useEffect(
    () => setParams((prev) => {
      applyDashBoardCondition(kojinDashBoardConfigure, conditions, prev)
      return prev
    }),
    [conditions, setParams]
  )

  useEffect(
    () => {
      const board = refBoard.current
      if (board) {
        if (panelViewMode === 'full' || panelViewMode === 'board') {
          if (!board.isExpanded()) board.expand()
        } else {
          if (!board.isCollapsed()) board.collapse()
        }
      }
      const list = refList.current
      if (list) {
        if (panelViewMode === 'full' || panelViewMode === 'list') {
          if (!list.isExpanded()) list.expand()
        } else {
          if (!list.isCollapsed()) list.collapse()
        }
      }
    },
    [refBoard, refList, panelViewMode]
  )
  const onPanelResize = useCallback(
    (size: number/*, prevSize: number | undefined*/) => {
      //console.log(`panelSize: ${prevSize} -> ${size}`)
      setPanelViewSize(size)
    },
    [setPanelViewSize]
  )

  useEffect(
    () => {
      setDocumentTitle(
        'KOL分析',
        search.title,
        targetList ? (targetList.listName ?? undefined) :
        mrpProject ? (mrpProject.projectName ?? undefined) :
        lvProject ? (lvProject.projectName ?? undefined) : undefined,
        tenant.tenantName
      )
      const crumbs: BreadCrumbItem[] = []
      if (search.manufacturer?.manufacturerName) {
        crumbs.push({
          label: search.manufacturer.manufacturerName,
          url: `/${tenant.domain}/search/list#manufacturer-${search.manufacturer.manufacturerId}`
        })
      }
      crumbs.push({
        label: search.title,
        url: `/${tenant.domain}/search/${search.searchId}/home` + (
          targetList ? '?tabs=targetList' :
          (mrpProject || lvProject) ? '?tabs=project' : ''
        )
      })
      if (targetList) {
        crumbs.push({ label: targetList.listName })
      } else if (mrpProject) {
        crumbs.push({ label: mrpProject.projectName ?? 'MRPlus' })
      } else if (lvProject) {
        crumbs.push({ label: lvProject.projectName ?? 'Web講演会' })
      } else {
        crumbs.push({ label: 'KOL分析' })
      }
      setBreadCrumbs(crumbs)
      setActivePage('kojin')
    },
    [tenant, search, targetList, mrpProject, lvProject, setBreadCrumbs, setActivePage]
  )
  return (
    <Stack
      direction="column"
      overflow="hidden"
      position="relative"
      {...others}
    >
      {loading && <Loading percent={progress}/>}
      <ErrorBlock error={error} padding={1}/>

      <PanelGroup
        direction="horizontal"
        autoSaveId="kojin_analytics"
        style={{
          height: '100%',
          maxHeight: '100%',
          flexGrow: 1,
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}
      >
        <Panel collapsible defaultSize={panelViewSize} ref={refBoard} onResize={onPanelResize}>
          <Stack
            direction="column"
            overflow="hidden"
            height="100%"
            position="relative"
          >
            <Paper square style={{ zIndex: 1 }}>
              <DashBoardBar
                search={search}
                dashBoard={dashBoard}
                targetList={targetList ?? undefined}
                leftItems={
                  (targetList && kojinList && counts && (counts.extend ?? 0) > 0) ? (
                    <TargetListExtendBar
                      searchId={search.searchId}
                      targetList={targetList}
                      count={kojinList.length}
                      source={counts.source}
                      extend={counts.extend}
                      extended={extended}
                      extendThresholdRate={extendThresholdRate}
                      setExtendThreshold={setExtendThreshold}
                    />
                  ) : kojinList ? (
                    <ValueSpan text={numberFormat(kojinList.length, '-')} suffix='人' stackProps={{ paddingX: 1 }}/>
                  ) : <></>
                }
                rightItems={
                  (panelViewMode === 'board') ? (
                    <>
                      {(search?.clients?.length ?? 0) > 0 && (
                        <PredictionBtn
                          state={predictionState}
                          kojinList={filteredList}
                          onClick={(e) => {
                            e.preventDefault()
                            openPredictionDialog()
                          }}
                        />
                      )}
                      <ValueSpan text={numberFormat(filteredList.length, '-')} suffix='人' stackProps={{ paddingX: 1 }}/>
                      <IconButton onClick={() => setPanelViewMode('full')}><ArrowLeftIcon/></IconButton>
                    </>
                  ) : (
                    <IconButton onClick={() => setPanelViewMode('board')}><ArrowRightIcon/></IconButton>
                  )
                }
              />
            </Paper>
            <DashBoard
              dashBoard={dashBoard}
              search={search ?? undefined}
              targetList={targetList ?? undefined}
              dataList={kojinList ?? []}
              onSelected={openKojin}
              defaultProfileId={profileId ?? undefined}
              flexGrow={1}
            />
          </Stack>
        </Panel>
        {panelViewMode === 'full' && (
          <PanelResizeHandle
            style={{
              width: '10px',
              background: 'white',
              boxShadow: 'rgba(0, 0, 0, 0.3) -4px 0 4px -2px inset, rgba(0, 0, 0, 0.2) 3px 0 2px -3px inset',
              boxSizing: 'border-box'
            }}
          />
        )}
        <Panel collapsible defaultSize={33} ref={refList}>
          <Paper square sx={{ height: '100%' }}>
            <KojinList
              kojinList={filteredList}
              predictionState={predictionState}
              openPredictionDialog={openPredictionDialog}
              viewMode={panelViewMode}
              onClickViewMode={setPanelViewMode}
            />
          </Paper>
        </Panel>
      </PanelGroup>

      {(selectedKojinCode || selectedAuthorId) ? (
        <KojinPreviewDialog
          kojinList={filteredList}
          kojinCode={selectedKojinCode ?? undefined}
          authorId={selectedAuthorId ?? undefined}
          onClose={closeKojinDialog}
        />
      ) : (modal?.type === 'prediction') ? (
        <PredictionDialog
          manufacturerId={search.manufacturer?.manufacturerId}
          predictionCondition={modal.predictionCondition ?? predictionState.predictionCondition}
          predictionState={predictionState}
          onClickHistory={openPredictionHistoryDialog}
          onClose={() => setModal(null)}
        />
      ) : (modal?.type === 'predictionHistory') ? (
        <PredictionHistoryDialog
          predictionHistory={predictionState.predictionHistory}
          setPredictionHistory={predictionState.setPredictionHistory}
          onSelectedCondition={onSelectedPredictionCondition}
          onClose={() => setModal(null)}
        />
      ) : <></>}
    </Stack>
  )
}
